import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon
} from '@mui/icons-material';

// Importar el servicio directamente desde la instancia
import { appointmentServiceInstance } from '../services/appointmentTimelineService';

// Componente CallButton independiente y funcional
export const CallButton = ({ 
  appointment, 
  onCallPatient,
  onStatusChange,
  onReloadAppointments, // Nueva prop para recargar página
  size = "small", 
  sx = {},
  title = "Call Patient",
  ...props 
}) => {
  const [callAlert, setCallAlert] = useState({
    open: false,
    patientName: '',
    appointment: null
  });

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleCall = async (appointmentData) => {
    console.log('📞 Attempting to call patient:', appointmentData?.patientName);
    
    try {
      setIsUpdatingStatus(true);

      // 1. Mostrar el diálogo de llamada inmediatamente
      setCallAlert({
        open: true,
        patientName: appointmentData?.patientName || 'Paciente sin nombre',
        appointment: appointmentData
      });
      
      // 2. Cambiar estado a "Called" automáticamente usando el servicio
      if (appointmentData?.status !== 'Called' && appointmentData?.id) {
        console.log('🔄 Updating appointment status to Called...');
        
        try {
          const updatedAppointment = await appointmentServiceInstance.updateAppointmentStatus(
            appointmentData.id, 
            'Called'
          );
          
          console.log('✅ Appointment status updated to Called:', updatedAppointment);
          
          // 3. Ejecutar callback del padre para actualizar estado local
          if (onStatusChange) {
            onStatusChange(appointmentData.id, 'Called');
          }
          
        } catch (statusError) {
          console.error('❌ Error updating status to Called:', statusError);
          // Continuar mostrando el diálogo aunque falle la actualización
        }
      }
      
      // 4. Ejecutar callback del padre si existe
      if (onCallPatient) {
        onCallPatient(appointmentData);
      }
      
    } catch (error) {
      console.error('❌ Error calling patient:', error);
      alert(`Error al llamar al paciente: ${error.message}`);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCloseCallAlert = () => {
    setCallAlert({
      open: false,
      patientName: '',
      appointment: null
    });
  };

  const handleEndCall = async () => {
    try {
      if (callAlert.appointment?.id) {
        console.log('📞 Ending call, updating status to Contacted...');
        
        setIsUpdatingStatus(true);
        
        try {
          // Cambiar estado a "Contacted" cuando termina la llamada
          const updatedAppointment = await appointmentServiceInstance.updateAppointmentStatus(
            callAlert.appointment.id, 
            'Contacted'
          );
          
          console.log('✅ Appointment status updated to Contacted:', updatedAppointment);
          
          if (onStatusChange) {
            onStatusChange(callAlert.appointment.id, 'Contacted');
          }
          
          // Recargar datos después de terminar la llamada
          if (onReloadAppointments) {
            setTimeout(() => {
              onReloadAppointments();
            }, 500);
          } else {
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
          
        } catch (statusError) {
          console.error('❌ Error updating status to Contacted:', statusError);
          alert(`Error al finalizar la llamada: ${statusError.message}`);
        }
      }
    } catch (error) {
      console.error('❌ Error ending call:', error);
      alert(`Error al finalizar la llamada: ${error.message}`);
    } finally {
      setIsUpdatingStatus(false);
    }
    
    handleCloseCallAlert();
  };

  // Validar que el appointment tiene los datos mínimos necesarios
  const isValidAppointment = appointment && (appointment.id || appointment._id);

  return (
    <>
      <IconButton
        size={size}
        disabled={!isValidAppointment || isUpdatingStatus}
        sx={{ 
          backgroundColor: isValidAppointment ? '#00AA00' : '#cccccc',
          color: 'white',
          '&:hover': { 
            backgroundColor: isValidAppointment ? '#008800' : '#cccccc' 
          },
          '&:disabled': {
            backgroundColor: '#cccccc',
            color: 'white'
          },
          width: size === 'small' ? 24 : 32,
          height: size === 'small' ? 24 : 32,
          ...sx
        }}
        onClick={(e) => {
          e.stopPropagation(); // Prevenir propagación del evento
          if (isValidAppointment) {
            handleCall(appointment);
          }
        }}
        title={isValidAppointment ? title : "Appointment data missing"}
        {...props}
      >
        <PhoneIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />
      </IconButton>

      {/* Diálogo de llamada */}
      <Dialog
        open={callAlert.open}
        onClose={handleCloseCallAlert}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 350,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <PhoneIcon sx={{ fontSize: 40, color: '#00AA00', mb: 1 }} />
          <Typography variant="h6">Calling Patient</Typography>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Calling <strong>{callAlert.patientName}</strong>...
          </Typography>
          
          {isUpdatingStatus && (
            <Typography variant="body2" color="info.main" sx={{ mb: 2 }}>
              🔄 Updating status...
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Status will be updated to "Called"
          </Typography>
          
          <Typography variant="caption" color="success.main" sx={{ mb: 2, display: 'block' }}>
            📞 Patient will be marked as Called
          </Typography>
          
          {callAlert.appointment && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 1,
              textAlign: 'left'
            }}>
              <Typography variant="caption" color="text.secondary">
                Appointment Details:
              </Typography>
              <Typography variant="body2">
                ID: {callAlert.appointment.appointmentId || callAlert.appointment.id || 'N/A'}
              </Typography>
              <Typography variant="body2">
                Date: {callAlert.appointment.date ? 
                  new Date(callAlert.appointment.date).toLocaleDateString('es-ES') : 
                  'No date'
                }
              </Typography>
              <Typography variant="body2">
                Time: {callAlert.appointment.startTime && callAlert.appointment.endTime ? 
                  `${callAlert.appointment.startTime} - ${callAlert.appointment.endTime}` : 
                  'No schedule'
                }
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                Status: {callAlert.appointment.status || 'Called'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
          <Button 
            //onClick={handleEndCall}
            onClick={handleCloseCallAlert}
            variant="contained"
            color="error"
            startIcon={<CallEndIcon />}
            sx={{ minWidth: 120 }}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus ? 'Updating...' : 'End Call'}
          </Button>
          <Button 
            onClick={handleCloseCallAlert} 
            variant="outlined"
            sx={{ minWidth: 100 }}
            disabled={isUpdatingStatus}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Componente principal (mantenido por compatibilidad)
const CallPatientComponent = ({ 
  onCallPatient, 
  onStatusChange,
  onReloadAppointments 
}) => {
  return null; // Este componente ya no es necesario con la nueva implementación
};

export default CallPatientComponent;